
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import './AuthPages.css';

const LoginPage = () => {
  const [form, setForm] = useState({
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const { success, error } = useToast();
  const navigate = useNavigate();

  // Handle login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const email = form.email.trim().toLowerCase();

    // Validate email
    if (!email) {
      setFieldErrors({
        email: 'Please enter your email address.'
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setFieldErrors({
        email: 'Please enter a valid email address.'
      });
      return;
    }

    // Validate password
    if (!form.password) {
      setFieldErrors({
        password: 'Please enter your password.'
      });
      return;
    }

    setLoading(true);

    try {
      const user = await login(email, form.password);

      success(
        `Welcome back${user?.first_name ? ', ' + user.first_name : ''}!`
      );

      // Redirect based on user role
      if (
        user?.is_staff ||
        user?.is_superuser ||
        user?.role === 'admin'
      ) {
        navigate('/admin');
      } else {
        navigate('/');
      }

    } catch (err) {

      // Support different API error formats
      const status =
        err.response?.status ??
        err.status ??
        err.statusCode;

      const data =
        err.response?.data ??
        err.data ??
        err.response?.body ??
        {};

      const rawMsg = String(
        data?.detail ||
        data?.message ||
        data?.error ||
        data?.non_field_errors?.[0] ||
        data?.email?.[0] ||
        data?.password?.[0] ||
        err.message ||
        ''
      ).toLowerCase();

      // Log details for debugging (not displayed to customers)
      console.error('LOGIN ERROR:', err);
      console.log('LOGIN STATUS:', status);
      console.log('LOGIN RESPONSE:', data);

      // Incorrect email or password
      if (
        status === 401 ||
        status === 400 ||
        rawMsg.includes('invalid credentials') ||
        rawMsg.includes('incorrect password') ||
        rawMsg.includes('invalid password') ||
        rawMsg.includes('no active account') ||
        rawMsg.includes('authentication failed') ||
        rawMsg.includes('wrong password')
      ) {
        const message =
          'Incorrect email or password. Please check your details and try again.';

        setFieldErrors({
          password: message
        });

        error(message);
      }

      // Account not found
      else if (
        status === 404 ||
        rawMsg.includes('user does not exist') ||
        rawMsg.includes('no account')
      ) {
        const message =
          'We couldn’t find an account with this email. Please check your email or register.';

        setFieldErrors({
          email: message
        });

        error(message);
      }

      // Network or server error
      else {
        const message =
          'Unable to sign in right now. Please check your internet connection and try again later.';

        setFieldErrors({
          password: message
        });

        error(message);
      }

    } finally {
      setLoading(false);
    }
  };

  // Clear field errors when the user edits a field
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  return (
    <div className="auth-page page-wrapper">

      <div className="auth-container">

        {/* Brand */}
        <div className="auth-brand">
          <span className="auth-logo-main">NAYRA</span>
          <span className="auth-logo-sub">FASHIONS</span>
        </div>

        {/* Heading */}
        <h1 className="auth-title">Welcome Back</h1>

        <p className="auth-subtitle">
          Sign in to your account
        </p>

        <form
          className="auth-form"
          onSubmit={handleSubmit}
          noValidate
        >

          {/* Email Field */}
          <div
            className={`form-group ${
              fieldErrors.email ? 'form-group--error' : ''
            }`}
          >
            <label htmlFor="email">
              EMAIL ADDRESS
            </label>

            <input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => {
                setForm(prev => ({
                  ...prev,
                  email: e.target.value
                }));

                clearFieldError('email');
              }}
              placeholder="your@email.com"
              className={
                fieldErrors.email ? 'input--error' : ''
              }
              autoComplete="email"
              disabled={loading}
            />

            {fieldErrors.email && (
              <span className="field-error" role="alert">
                <span className="field-error-icon">!</span>
                {fieldErrors.email}
              </span>
            )}
          </div>

          {/* Password Field */}
          <div
            className={`form-group ${
              fieldErrors.password ? 'form-group--error' : ''
            }`}
          >
            <label htmlFor="password">
              PASSWORD
            </label>

            <div className="input-wrapper">

              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={form.password}
                onChange={(e) => {
                  setForm(prev => ({
                    ...prev,
                    password: e.target.value
                  }));

                  clearFieldError('password');
                }}
                placeholder="••••••••"
                className={
                  fieldErrors.password ? 'input--error' : ''
                }
                autoComplete="current-password"
                disabled={loading}
              />

              {/* Show / Hide Password */}
              <button
                type="button"
                className="input-toggle"
                onClick={() => setShowPassword(prev => !prev)}
                tabIndex={-1}
                aria-label={
                  showPassword ? 'Hide password' : 'Show password'
                }
              >
                {showPassword ? '🙈' : '👁'}
              </button>

            </div>

            {fieldErrors.password && (
              <span className="field-error" role="alert">
                <span className="field-error-icon">!</span>
                {fieldErrors.password}
              </span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-gold auth-btn"
            disabled={loading}
          >
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>

        </form>

        {/* Divider */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* Register Link */}
        <p className="auth-switch">
          Don't have an account?{' '}
          <Link to="/register">
            Create one
          </Link>
        </p>

      </div>

      {/* Right Side Visual */}
      <div className="auth-visual">

        <div className="auth-visual-overlay" />

        <div className="auth-visual-text">
          <p>Elegance</p>
          <p>Redefined</p>
        </div>

      </div>

    </div>
  );
};

export default LoginPage;
